/**
 * The timesheet + time-clock refusals (`{ error, code }` from
 * `/api/v1/timesheets/*`) turned into translated toasts — one place for every
 * hook that writes a timesheet, a time entry or a clock (`useTimesheets`,
 * `useTimeClock`).
 *
 * A coded refusal gets its own sentence (`timesheets:toasts.<code>`); anything
 * else the caller's fallback key, with the server message as the description.
 * Keys live in the PRELOADED `timesheets` namespace, so a toast fired from the
 * lazily-loaded time clock never renders a raw key. Precedent:
 * `lib/membershipErrors.ts`.
 */
import { TIMESHEET_ERROR_CODES, type TimesheetErrorCode } from '@releve/shared';
import type { EnvelopeError } from '@/lib/api';
import { toast } from '@/hooks/useToast';
import i18n from '@/i18n';

/** One toast per refusal code (literal keys, so the locale check sees them). */
const CODED_TOAST_KEYS: Record<TimesheetErrorCode, string> = {
  [TIMESHEET_ERROR_CODES.alreadyClockedIn]: 'timesheets:toasts.alreadyClockedIn',
  [TIMESHEET_ERROR_CODES.hasOthersEntries]: 'timesheets:toasts.hasOthersEntries',
  [TIMESHEET_ERROR_CODES.endTimeRequired]: 'timesheets:toasts.endTimeRequired',
  [TIMESHEET_ERROR_CODES.entriesChanged]: 'timesheets:toasts.entriesChanged',
  [TIMESHEET_ERROR_CODES.adminsVisibilityForbidden]: 'timesheets:toasts.adminsVisibilityForbidden',
};

/**
 * Refusals are timesheet rules, not bugs: App.tsx's MutationCache.onError
 * skips Sentry for them. Spread as the `meta` of every timesheet mutation.
 */
export const TIMESHEET_MUTATION_META = {
  expectedErrorCodes: Object.values(TIMESHEET_ERROR_CODES) as readonly string[],
} as const;

/** The refusal code a failed timesheet request carries, or `null`. */
export function timesheetErrorCodeOf(error: unknown): TimesheetErrorCode | null {
  const code = (error as EnvelopeError<{ code?: string }> | null | undefined)?.envelope?.code;
  return code && Object.prototype.hasOwnProperty.call(CODED_TOAST_KEYS, code)
    ? (code as TimesheetErrorCode)
    : null;
}

/**
 * The toast for a failed timesheet request: a coded refusal's own sentence,
 * else `fallbackKey` with the server message as the description.
 */
export function timesheetErrorMessage(
  error: unknown,
  fallbackKey: string,
): { message: string; description?: string } {
  const code = timesheetErrorCodeOf(error);
  if (code) return { message: i18n.t(CODED_TOAST_KEYS[code]) };
  const serverMessage = error instanceof Error ? error.message : '';
  return serverMessage
    ? { message: i18n.t(fallbackKey), description: serverMessage }
    : { message: i18n.t(fallbackKey) };
}

/** `toast.error` of {@link timesheetErrorMessage}. */
export function toastTimesheetError(error: unknown, fallbackKey: string): void {
  const { message, description } = timesheetErrorMessage(error, fallbackKey);
  toast.error(message, description ? { description } : undefined);
}
